import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ArrowRight, Calendar, Clock, Loader2, Tag, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { BlogCard } from '../components/BlogCard';
import { ShareButtons } from '../components/ShareButtons';
import { MarkdownContent } from '../components/MarkdownContent';
import { API, resolveImageUrl, formatDate } from '../lib/api';
import { useLanguage } from '../i18n/LanguageContext';

const BlogPost = () => {
  const { slug } = useParams();
  const { t, lang } = useLanguage();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    setStatus('loading');
    axios.get(`${API}/blog/${slug}`)
      .then(res => { setPost(res.data.post); setRelated(res.data.related); setStatus('ok'); })
      .catch(() => setStatus('error'));
  }, [slug]);

  if (status === 'loading') {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-gray-500 gap-2" data-testid="blog-post-loading">
        <Loader2 className="w-5 h-5 animate-spin" /> {t('blog.loading')}
      </div>
    );
  }

  if (status === 'error' || !post) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4" data-testid="blog-post-not-found">{t('blog.notFound')}</h2>
          <Link to="/blog"><Button className="bg-[#8B2C3A] hover:bg-[#6B1C2A] text-white">{t('blog.back')}</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <SEO title={post.title} description={post.excerpt} keywords={(post.tags || []).join(', ')} />

      <div className="bg-gray-50 border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link to="/blog" data-testid="blog-back-link" className="inline-flex items-center text-[#8B2C3A] hover:text-[#6B1C2A] transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />{t('blog.back')}
          </Link>
        </div>
      </div>

      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <span className="inline-block bg-[#8B2C3A]/10 text-[#8B2C3A] text-xs font-semibold px-3 py-1 rounded-full mb-4">{post.category}</span>
        <h1 data-testid="blog-post-title" className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">{post.title}</h1>
        <div className="flex flex-wrap items-center gap-5 text-sm text-gray-500 mb-6">
          <span className="inline-flex items-center gap-1.5"><User className="w-4 h-4" />{post.author}</span>
          <span className="inline-flex items-center gap-1.5"><Calendar className="w-4 h-4" />{formatDate(post.created_at, lang)}</span>
          <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4" />{post.read_time} {t('blog.minRead')}</span>
        </div>

        <div className="mb-8">
          <ShareButtons title={post.title} compact />
        </div>

        {post.cover_image && (
          <div className="rounded-2xl overflow-hidden shadow-xl mb-10 aspect-[16/9] bg-gray-100">
            <img src={resolveImageUrl(post.cover_image)} alt={post.title} className="w-full h-full object-cover" />
          </div>
        )}

        <p className="text-lg md:text-xl text-gray-600 leading-relaxed border-l-4 border-[#8B2C3A] pl-5 mb-10">{post.excerpt}</p>

        <MarkdownContent content={post.content} />

        <div className="mt-10">
          <ShareButtons title={post.title} />
        </div>

        {post.tags?.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mt-8 pt-8 border-t border-gray-200">
            <Tag className="w-4 h-4 text-gray-400" />
            {post.tags.map(tag => (
              <span key={tag} className="bg-gray-100 text-gray-700 text-xs px-3 py-1 rounded-full">{tag}</span>
            ))}
          </div>
        )}
      </article>

      <section className="py-16 bg-gradient-to-r from-[#8B2C3A] to-[#6B1C2A] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">{t('blog.ctaTitle')}</h2>
          <p className="text-xl mb-8 text-gray-200">{t('blog.ctaSub')}</p>
          <Link to="/contact">
            <Button size="lg" className="bg-[#D4AF37] hover:bg-[#B8941F] text-gray-900 font-semibold">
              {t('blog.ctaButton')}<ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {related.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">{t('blog.related')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {related.map((p, i) => <BlogCard key={p.id} post={p} index={i} />)}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default BlogPost;
